import React from 'react';
import { Link } from 'react-router-dom';
import Layout, { TopBar } from '../components/Layout';
import { Meta, Display, Chip, Rule, Button } from '../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useApi, useMultipleApi } from '../lib/api';
import { Sprout } from '../components/Illustrations';

/**
 * Weather - pełny ekran pogody dla ogrodnika.
 *
 * Endpointy:
 *   GET /api/weather/current        - aktualna pogoda + lokalizacja
 *   GET /api/weather/forecast       - 5-dniowa prognoza (hourly + daily)
 *   GET /api/weather/recommendations - rekomendacje + alerty
 *   GET /api/calendar/moon/current  - faza księżyca + porady ogrodnicze
 *
 * Sekcje:
 *   - Hero: aktualna temperatura, miasto, opis
 *   - Alerty: mróz, upał, wiatr, opady
 *   - Prognoza dzienna (5 dni)
 *   - Prognoza godzinowa (24h)
 *   - Rekomendacje ogrodowe
 *   - Faza księżyca + co robić
 */

const WEEKDAYS_PL = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
const WEEKDAYS_PL_SHORT = ['Nd', 'Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob'];

function dayLabel(dateStr) {
  // dateStr w formacie "DD.MM.YYYY" z polskiego locale
  const parts = dateStr.split('.');
  if (parts.length !== 3) return dateStr;
  const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  if (diff === 0) return 'Dziś';
  if (diff === 1) return 'Jutro';
  if (diff === 2) return 'Pojutrze';
  return WEEKDAYS_PL[d.getDay()];
}

// Mapowanie OpenWeather icon → emoji (tymczasowe, można zastąpić SVG)
function weatherEmoji(icon, description) {
  if (!icon) return '☁';
  const id = icon.replace(/[dn]$/, '');
  const map = {
    '01': '☀',  // clear sky
    '02': '🌤',  // few clouds
    '03': '⛅',  // scattered clouds
    '04': '☁',  // broken clouds
    '09': '🌧',  // shower rain
    '10': '🌦',  // rain
    '11': '⛈',  // thunderstorm
    '13': '❄',  // snow
    '50': '🌫',  // mist
  };
  return map[id] || '☁';
}

// ── Hero z aktualną temperaturą ─────────────────────────────────────
function WeatherHero({ current, location }) {
  if (!current) return null;
  return (
    <div style={{
      padding: '32px 28px',
      borderRadius: 18,
      background: 'linear-gradient(135deg, var(--t-bg-elev) 0%, rgba(168, 196, 158, 0.06) 100%)',
      border: '1px solid var(--t-line)',
      marginBottom: 28,
    }}>
      <Meta color="sage" style={{ marginBottom: 14 }}>{location?.city || 'Twoja lokalizacja'} · teraz</Meta>
      <div style={{
        display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 84, lineHeight: 1, flexShrink: 0 }}>
          {weatherEmoji(current.icon, current.description)}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Display size={'clamp(56px, 9vw, 88px)'} style={{
            display: 'block', letterSpacing: '-.04em', lineHeight: 0.95,
            color: 'var(--t-text)',
          }}>{current.temperature}°</Display>
          <div style={{ fontSize: 16, color: 'var(--t-text-dim)', marginTop: 6 }}>
            {current.description ? current.description.charAt(0).toUpperCase() + current.description.slice(1) : '—'}
            {current.feelsLike != null && current.feelsLike !== current.temperature && (
              <span style={{ color: 'var(--t-text-mute)', fontSize: 13, marginLeft: 8 }}>
                · odczuwalna {current.feelsLike}°
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        gap: 14, marginTop: 24, paddingTop: 20,
        borderTop: '1px solid var(--t-line)',
      }}>
        {[
          { l: 'Wiatr', v: current.windSpeed != null ? `${current.windSpeed} km/h` : '—' },
          { l: 'Wilgotność', v: current.humidity != null ? `${current.humidity}%` : '—' },
          { l: 'Opady', v: current.rain > 0 ? `${current.rain.toFixed(1)} mm` : 'Bez' },
          { l: 'Zachmurzenie', v: current.clouds != null ? `${current.clouds}%` : '—' },
        ].map(s => (
          <div key={s.l}>
            <Meta style={{ marginBottom: 4 }}>{s.l}</Meta>
            <div style={{
              fontFamily: 'var(--t-mono)', fontSize: 15,
              color: 'var(--t-text)', letterSpacing: '.04em',
            }}>{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Alert (mróz/upał/wiatr) ─────────────────────────────────────────
function AlertCard({ alert }) {
  const tone = alert.severity === 'critical' || alert.type === 'frost' ? 'rust' :
               alert.severity === 'warning' || alert.type === 'severe-heat' ? 'amber' : 'sage';
  const color = tone === 'rust' ? 'var(--t-rust)' :
                tone === 'amber' ? 'var(--t-amber)' : 'var(--t-sage)';
  const bg = tone === 'rust' ? 'rgba(192, 106, 74, 0.08)' :
             tone === 'amber' ? 'rgba(220, 180, 100, 0.08)' :
             'rgba(168, 196, 158, 0.06)';
  const icon = alert.type?.includes('frost') ? '❄' :
               alert.type?.includes('heat') ? '🔥' :
               alert.type?.includes('wind') ? '💨' :
               alert.type?.includes('rain') || alert.type?.includes('storm') ? '🌧' : '⚠';

  return (
    <div style={{
      padding: '16px 18px', borderRadius: 12,
      background: bg, borderLeft: `3px solid ${color}`,
      marginBottom: 10,
      display: 'flex', alignItems: 'flex-start', gap: 14,
    }}>
      <div style={{ fontSize: 24, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, color: 'var(--t-text)', fontWeight: 500,
          letterSpacing: '-.01em', marginBottom: 4,
        }}>{alert.title || alert.message}</div>
        {alert.message && alert.title && alert.message !== alert.title && (
          <div style={{
            fontSize: 13, color: 'var(--t-text-dim)',
            lineHeight: 1.5,
          }}>{alert.message}</div>
        )}
        {alert.recommendation && (
          <div style={{
            marginTop: 8, padding: '8px 12px', borderRadius: 8,
            background: 'var(--t-bg)',
            fontSize: 12, color: 'var(--t-text-dim)',
            fontStyle: 'italic', lineHeight: 1.45,
          }}>↳ {alert.recommendation}</div>
        )}
      </div>
    </div>
  );
}

// ── Karta dnia w prognozie ─────────────────────────────────────────
function DayCard({ day, isToday }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 12,
      background: isToday ? 'rgba(168, 196, 158, 0.08)' : 'var(--t-bg-elev)',
      border: `1px solid ${isToday ? 'var(--t-sage)' : 'var(--t-line)'}`,
      display: 'flex', flexDirection: 'column',
      minHeight: 130, gap: 6,
    }}>
      <div style={{
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.18em', textTransform: 'uppercase',
        color: isToday ? 'var(--t-sage)' : 'var(--t-text-mute)',
      }}>{dayLabel(day.date)}</div>
      <div style={{ fontSize: 36, lineHeight: 1, marginTop: 4 }}>
        {weatherEmoji(day.icon, day.description)}
      </div>
      <div style={{ marginTop: 'auto' }}>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 6,
        }}>
          <span style={{
            fontFamily: 'var(--t-display)', fontSize: 22,
            color: 'var(--t-text)', letterSpacing: '-.02em',
          }}>{day.tempMax}°</span>
          <span style={{
            fontFamily: 'var(--t-mono)', fontSize: 13,
            color: 'var(--t-text-mute)',
          }}>{day.tempMin}°</span>
        </div>
        {day.pop > 20 && (
          <div style={{
            fontSize: 11, color: 'var(--t-sage)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            marginTop: 4,
          }}>💧 {day.pop}%{day.rain > 0 && ` · ${day.rain.toFixed(1)} mm`}</div>
        )}
        {day.wind > 30 && (
          <div style={{
            fontSize: 11, color: 'var(--t-amber)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            marginTop: 2,
          }}>💨 {day.wind} km/h</div>
        )}
      </div>
    </div>
  );
}

// ── Slot godzinowy ──────────────────────────────────────────────────
function HourSlot({ slot, isFirst }) {
  const hour = slot.time?.split(':')[0] || '?';
  return (
    <div style={{
      flexShrink: 0, width: 72,
      padding: '12px 8px', borderRadius: 10,
      background: 'var(--t-bg-elev)',
      border: `1px solid ${isFirst ? 'var(--t-sage)' : 'var(--t-line)'}`,
      textAlign: 'center',
    }}>
      <div style={{
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.12em', color: 'var(--t-text-mute)',
        marginBottom: 6,
      }}>{isFirst ? 'Teraz' : `${hour}:00`}</div>
      <div style={{ fontSize: 20, lineHeight: 1 }}>
        {weatherEmoji(slot.icon, slot.description)}
      </div>
      <div style={{
        fontFamily: 'var(--t-display)', fontSize: 16,
        color: 'var(--t-text)', marginTop: 6,
      }}>{slot.temperature}°</div>
      {slot.pop > 30 && (
        <div style={{
          fontSize: 10, color: 'var(--t-sage)',
          fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
          marginTop: 3,
        }}>{slot.pop}%</div>
      )}
    </div>
  );
}

// ── Faza księżyca ───────────────────────────────────────────────────
function MoonCard({ data }) {
  if (!data) return null;
  const { moon, gardening } = data;
  return (
    <div style={{
      padding: '24px 26px', borderRadius: 14,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 18,
        marginBottom: 18,
      }}>
        <div style={{ fontSize: 56, lineHeight: 1 }}>{moon.emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Display size={22} style={{ display: 'block', letterSpacing: '-.02em' }}>
            {moon.phaseName}
          </Display>
          <div style={{
            fontSize: 12, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.08em',
            marginTop: 4,
          }}>
            Oświetlenie {Math.round(moon.illumination)}%
            {gardening?.seasonInfo && ` · ${gardening.seasonInfo}`}
          </div>
        </div>
      </div>

      {gardening?.general && (
        <div style={{
          padding: '12px 14px', borderRadius: 10,
          background: 'var(--t-bg)',
          fontSize: 13, color: 'var(--t-text-dim)',
          lineHeight: 1.5, marginBottom: 16,
          fontStyle: 'italic',
        }}>{gardening.general}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <Meta color="sage" style={{ marginBottom: 8 }}>Sprzyja</Meta>
          {gardening?.favorable?.length > 0 ? (
            <ul style={{
              padding: 0, margin: 0, listStyle: 'none',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              {gardening.favorable.map((f, i) => (
                <li key={i} style={{
                  fontSize: 12, color: 'var(--t-text-dim)',
                  paddingLeft: 12, position: 'relative', lineHeight: 1.45,
                }}>
                  <span style={{
                    position: 'absolute', left: 0,
                    color: 'var(--t-sage)',
                  }}>·</span>
                  {f}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--t-text-mute)', fontStyle: 'italic' }}>—</div>
          )}
        </div>
        <div>
          <Meta color="rust" style={{ marginBottom: 8 }}>Niewskazane</Meta>
          {gardening?.unfavorable?.length > 0 ? (
            <ul style={{
              padding: 0, margin: 0, listStyle: 'none',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              {gardening.unfavorable.map((f, i) => (
                <li key={i} style={{
                  fontSize: 12, color: 'var(--t-text-dim)',
                  paddingLeft: 12, position: 'relative', lineHeight: 1.45,
                }}>
                  <span style={{
                    position: 'absolute', left: 0,
                    color: 'var(--t-rust)',
                  }}>·</span>
                  {f}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--t-text-mute)', fontStyle: 'italic' }}>—</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Rekomendacja ogrodowa ──────────────────────────────────────────
function RecommendationRow({ rec }) {
  const toneMap = {
    spraying: 'amber',
    watering: 'sage',
    sowing: 'sage',
    planting: 'sage',
    harvesting: 'amber',
  };
  const tone = toneMap[rec.type] || 'sage';
  const color = tone === 'amber' ? 'var(--t-amber)' : 'var(--t-sage)';
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '12px 14px', borderRadius: 10,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      borderLeft: `3px solid ${color}`,
      marginBottom: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, color: 'var(--t-text)',
          letterSpacing: '-.01em', marginBottom: 3,
        }}>{rec.title || rec.message}</div>
        {rec.message && rec.title && rec.message !== rec.title && (
          <div style={{ fontSize: 12, color: 'var(--t-text-dim)', lineHeight: 1.45 }}>
            {rec.message}
          </div>
        )}
      </div>
      <Chip tone={tone}>{rec.priority || rec.type || 'porada'}</Chip>
    </div>
  );
}

// ── Główny komponent ────────────────────────────────────────────────
export default function Weather() {
  const { results, loading, errors } = useMultipleApi({
    current: '/api/weather/current',
    forecast: '/api/weather/forecast',
    recs: '/api/weather/recommendations',
  });
  const { data: moon } = useApi('/api/calendar/moon/current');

  if (errors.current?.includes('autoryz') || errors.current === 'Brak tokenu autoryzacji') {
    return <Layout><ErrorState error={{ status: 401, message: errors.current }} /></Layout>;
  }
  if (loading) return <Layout><LoadingState label="Ładuję pogodę…" /></Layout>;

  // Brak lokalizacji
  const locationMissing = errors.current?.includes('lokaliz') ||
                          errors.forecast?.includes('lokaliz') ||
                          (!results.current?.weather && errors.current?.includes('Lokalizacja'));

  if (locationMissing || !results.current?.weather) {
    return (
      <Layout>
        <div style={{
          maxWidth: 480, margin: '60px auto', padding: 24,
          textAlign: 'center',
        }}>
          <Sprout size={120} color="var(--t-line-2)" />
          <Display size={32} style={{
            display: 'block', marginTop: 24, marginBottom: 14,
            letterSpacing: '-.02em',
          }}>Pogoda potrzebuje Twojej lokalizacji</Display>
          <div style={{
            fontSize: 14, color: 'var(--t-text-dim)',
            lineHeight: 1.55, marginBottom: 28,
          }}>
            Bez współrzędnych nie pokażemy prognozy ani alertów mrozowych dla Twojego ogrodu.
            Ustaw miasto i pozycję w sekcji „Mój ogród".
          </div>
          <Link to="/profile/garden" style={{ textDecoration: 'none' }}>
            <Button variant="primary">Ustaw lokalizację →</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const current = results.current.weather;
  const location = results.current.location;
  const forecast = results.forecast;
  const recs = results.recs?.recommendations;

  const alerts = recs?.alerts || [];
  const recommendations = recs?.recommendations || [];
  const blockedTypes = recs?.blockedTypes || [];

  const todayStr = new Date().toLocaleDateString('pl-PL');
  const todayDaily = forecast?.daily?.find(d => d.date === todayStr);

  return (
    <Layout topBar={<TopBar
      left={<Meta>Pogoda · {location?.city || 'Twoja lokalizacja'}</Meta>}
    />}>
      <div style={{
        maxWidth: 980, margin: '0 auto',
        padding: '32px 32px 60px',
      }}>
        {/* Hero */}
        <WeatherHero current={current} location={location} />

        {/* Alerty */}
        {alerts.length > 0 && (
          <>
            <Rule label="I — uważaj" />
            <div style={{ marginBottom: 32 }}>
              {alerts.map((a, i) => <AlertCard key={i} alert={a} />)}
            </div>
          </>
        )}

        {/* Prognoza dzienna */}
        {forecast?.daily?.length > 0 && (
          <>
            <Rule label="II — prognoza dzienna" />
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
              gap: 10, marginBottom: 32,
            }}>
              {forecast.daily.slice(0, 5).map((d, i) => (
                <DayCard key={d.date} day={d} isToday={i === 0} />
              ))}
            </div>
          </>
        )}

        {/* Prognoza godzinowa (24h) */}
        {forecast?.hourly?.length > 0 && (
          <>
            <Rule label="III — najbliższe godziny" />
            <div style={{
              display: 'flex', gap: 8, overflowX: 'auto',
              padding: '4px 0 14px',
              marginBottom: 32,
              WebkitOverflowScrolling: 'touch',
            }}>
              {forecast.hourly.slice(0, 16).map((h, i) => (
                <HourSlot key={h.timestamp} slot={h} isFirst={i === 0} />
              ))}
            </div>
          </>
        )}

        {/* Rekomendacje */}
        {recommendations.length > 0 && (
          <>
            <Rule label="IV — co robić w ogrodzie" />
            <div style={{ marginBottom: 32 }}>
              {recommendations.slice(0, 8).map((r, i) => (
                <RecommendationRow key={i} rec={r} />
              ))}
            </div>
            {blockedTypes.length > 0 && (
              <div style={{
                padding: '12px 14px', borderRadius: 10,
                background: 'rgba(192, 106, 74, 0.06)',
                borderLeft: '3px solid var(--t-rust)',
                fontSize: 12, color: 'var(--t-text-dim)',
                marginTop: -22, marginBottom: 32,
              }}>
                ⚠ Dziś niewskazane: {blockedTypes.map(t =>
                  t === 'spraying' ? 'oprysk' :
                  t === 'watering' ? 'podlewanie' :
                  t === 'sowing' ? 'siew' : t
                ).join(', ')} — wstrzymaj się.
              </div>
            )}
          </>
        )}

        {/* Faza księżyca */}
        {moon && (
          <>
            <Rule label="V — faza księżyca" />
            <div style={{ marginBottom: 32 }}>
              <MoonCard data={moon} />
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{
          marginTop: 40, paddingTop: 20,
          borderTop: '1px solid var(--t-line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12,
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.12em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>
          <span>Dane: OpenWeather · {location?.city || ''}</span>
          <Link to="/profile/garden" style={{ color: 'var(--t-sage)', textDecoration: 'none' }}>
            Zmień lokalizację →
          </Link>
        </div>
      </div>
    </Layout>
  );
}
